import { useQuery, UseQueryResult } from '@tanstack/react-query';
import MojoApiFactory from '../../config/api-factory';
import { AxiosResponse } from 'axios';
import {
  HomepageResponse,
  isBannerModule,
  isContentModule,
} from '../../models/home-page-custom/types';
import { useTranslation } from 'react-i18next';
import HomepageShortcuts from '../../components/v2/homepage-shortcuts/homepage-shortcuts';
import './home.scss';
import { HomepageBanner } from './homepage-banner';
import { HomeFeeds } from './home-feeds';
import { HomeSection } from '../../components/v2/home-section';
import useFeatureFlag from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';
import useScreenTitle from '../../common/use-screen-title';
import { useRecoilState } from 'recoil';
import { homePageVisitAnalyticsSent } from '../../models/home-page/recoil-state';
import { usePatronSelector } from '../../common/use-patron-selector';
import { channelSelectors } from '../../models/channels';
import { useEffect } from 'react';
import { trackHomeLoad } from '../../models/analytics';
import { Alert } from '@socialchorus/shared-ui-components';

type CustomHomepageProps = {
  homepageQuery: UseQueryResult<HomepageResponse, unknown>;
};

export const UserHomepage = () => {
  const homepageQuery = useQuery({
    queryKey: ['customHomepageConfig'],
    queryFn: async () => {
      return (
        (await MojoApiFactory.optimus.get(
          `web_homepage`
        )) as AxiosResponse<HomepageResponse>
      )?.data;
    },
  });

  return <CustomHomepage homepageQuery={homepageQuery} />;
};

export const CustomHomepageById = ({
  homepageId,
  isPreviewMode = false,
}: {
  homepageId: string;
  isPreviewMode?: boolean;
}) => {
  const homepageQuery = useQuery({
    queryKey: ['customHomepageById', homepageId],
    queryFn: async () => {
      return (
        (await MojoApiFactory.optimus.get(
          `web_homepage/${homepageId}`
        )) as AxiosResponse<HomepageResponse>
      )?.data;
    },
  });

  return (
    <CustomHomepage
      homepageQuery={homepageQuery}
      isPreviewMode={isPreviewMode}
    />
  );
};

export const CustomHomepage = ({
  homepageQuery,
  isPreviewMode = false,
}: CustomHomepageProps & { isPreviewMode?: boolean }) => {
  useScreenTitle();
  const { t } = useTranslation();

  const [visitedAnalyticsSent, setVisitedAnalyticsSent] = useRecoilState(
    homePageVisitAnalyticsSent
  );
  useEffect(() => {
    if (!visitedAnalyticsSent) {
      trackHomeLoad();
      setVisitedAnalyticsSent(true);
    }
  }, [visitedAnalyticsSent, setVisitedAnalyticsSent]);

  const { data: homepageData, isLoading } = homepageQuery;

  // LEGACY - feature flags to enable the removal of legacy functionality from the home page.
  const newestTopicEnabled = useFeatureFlag(Feature.NEWEST_TOPIC_ENABLED);
  const recommendedTopicEnabled = useFeatureFlag(
    Feature.RECOMMENDED_TOPIC_ENABLED
  );
  const popularTopicEnabled = useFeatureFlag(Feature.POPULAR_TOPIC_ENABLED);
  const newTopicsString = useFeatureFlag(Feature.CHANNELS_TO_TOPICS_ENABLED);
  const recommendedChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqRecommended(state)
  );
  const newestChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqNewest(state)
  );
  const popularChannelId = usePatronSelector((state) =>
    channelSelectors.getChannelIdFirstUniqPopular(state)
  );

  if (isLoading || !homepageData) {
    return null;
  }

  const bannerModule = homepageData.attributes.modules?.find(isBannerModule);
  const contentModules =
    homepageData.attributes.modules?.filter(isContentModule);

  // Translation keys
  const recommendedChannelTitle = newTopicsString
    ? 'landing.recommended_topic'
    : 'landing.recommended_channel';
  const newestChannelTitle = newTopicsString
    ? 'landing.newest_topic'
    : 'landing.newest_channel';
  const mostPopularChannelString = newTopicsString
    ? 'landing.most_popular_topic'
    : 'landing.most_popular_channel';

  return (
    <div className="home page">
      {isPreviewMode && (
        <div className="homepagePreviewBannerWrapper">
          <Alert
            title={
              t('screens.home.homepage.preview_banner_title') ||
              'Homepage Preview'
            }
            description={t('screens.home.homepage.preview_banner_description')}
            type="info"
            iconName="info"
            size="compact"
          />
        </div>
      )}
      <div className="container">
        <h1 className="sr-only">{t('nav_links.home')}</h1>
        {!!bannerModule && <HomepageBanner bannerModule={bannerModule} />}
        <HomepageShortcuts topIsFlush={!bannerModule} />
        {contentModules && (
          <HomeFeeds
            homepageId={homepageData.attributes.id}
            contentModules={contentModules}
          />
        )}

        {/* LEGACY - Channels Section */}
        <div className="home__channels">
          <div className="container">
            {recommendedTopicEnabled && recommendedChannelId && (
              <>
                <HomeSection
                  title={t(recommendedChannelTitle)}
                  channelId={String(recommendedChannelId)}
                  minLength={1}
                  maxLength={4}
                  analyticsData={{ location: 'home_channel_recommended' }}
                />
              </>
            )}

            {newestTopicEnabled && newestChannelId && (
              <>
                <HomeSection
                  title={t(newestChannelTitle)}
                  channelId={newestChannelId}
                  minLength={1}
                  maxLength={4}
                  analyticsData={{ location: 'home_channel_newest' }}
                />
              </>
            )}

            {popularTopicEnabled && popularChannelId && (
              <>
                <HomeSection
                  title={t(mostPopularChannelString)}
                  channelId={popularChannelId}
                  minLength={1}
                  maxLength={4}
                  analyticsData={{ location: 'home_channel_popular' }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
